import type { FunctionComponentWithRef } from "react";
import { graphql, useFragment } from "react-relay";

import { withHighlightedHeadline } from "scmp-app/components/content/content-item-render/highlight/hoc";
import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "scmp-app/components/content/content-item-render/types";
import type { homeSuperSplashLargeSecondaryV2ContentItemContent$key } from "scmp-app/queries/__generated__/homeSuperSplashLargeSecondaryV2ContentItemContent.graphql";

import {
  ActionBar,
  Container,
  Headline,
  HeadlineContainer,
  LiveTagContainer,
  StyledEntityLink,
  SubHeadline,
} from "./styles";

export type Props = ContentItemRenderVariantProps & {
  reference: homeSuperSplashLargeSecondaryV2ContentItemContent$key;
  withComment?: boolean;
  withSubHeadline?: boolean;
};

const Component: FunctionComponentWithRef<Props, HTMLDivElement> = ({
  className,
  onClick,
  ref,
  reference,
  withComment = false,
  withSubHeadline = false,
}) => {
  // Fetch withSubHeadline unconditionally: its visibility is gated by the
  // `withSubHeadline` render prop, which depends on client-side responsive
  // state, so the data must already be present.
  const content = useFragment(
    graphql`
      fragment homeSuperSplashLargeSecondaryV2ContentItemContent on Content {
        entityId
        ...entityLink
        ...hooksContentItemProvidersContent
          @arguments(
            withHeadline: true
            withLiveTag: true
            withSubHeadline: true
            withActionBar: true
          )
      }
    `,
    reference,
  );
  const providers = useContentItemProviders(content);
  const subHeadline = providers.subHeadline();

  return (
    <Container className={className} ref={ref}>
      <StyledEntityLink onClick={() => onClick?.(content.entityId)} reference={content}>
        <HeadlineContainer>
          <LiveTagContainer>{providers.liveTag({ variant: "homeV2" })}</LiveTagContainer>
          <Headline>
            {providers.headline({
              preferSocialHeadline: true,
            })}
          </Headline>
        </HeadlineContainer>
        {withSubHeadline && subHeadline && <SubHeadline>{subHeadline}</SubHeadline>}
      </StyledEntityLink>
      <ActionBar>
        {providers.actionBar({
          commentColorVariant: "light-action-blue",
          commentIconHeight: 14,
          commentIconWidth: 14,
          isBookmarkVisible: false,
          isCommentsVisible: withComment ?? false,
          isIconVisible: false,
        })}
      </ActionBar>
    </Container>
  );
};

Component.displayName = "ContentItemHomeSuperSplashLargeSecondaryV2";

export const ContentItemHomeSuperSplashLargeSecondaryV2 = withHighlightedHeadline(Component);
